/**
 * Gestion des accès des étudiants au portail PWA.
 * KPIs + filtres rapides + export Excel + suivi des visites.
 */
import React, { useState, useMemo, useEffect } from 'react';
import * as XLSX from 'xlsx';
import {
  useListEtudiantsAccesQuery,
  useForcerAccesMutation,
  useResetPasswordEtudiantMutation,
  useBloquerEtudiantMutation,
  useMarquerContratMutation,
  useEncaisserPaiementMutation,
  useLazyGetHistoriqueConnexionsQuery,
  useNotifierEtudiantMutation,
  useBroadcastMessageMutation,
  useListBroadcastsRecentsQuery,
  useAnnulerBroadcastMutation,
  EtudiantAcces,
  ConnexionEvent,
} from '../../services/portailAdminApi';
import { EtatPaiementModal } from './EtatPaiementModal';

const STATUTS = [
  { key: '', label: 'Tous', color: 'gray' },
  { key: 'a_jour', label: 'À jour (autorisés)', color: 'green' },
  { key: 'sous_contrat', label: 'Sous contrat', color: 'amber' },
  { key: 'force_admin', label: 'Forcés par admin', color: 'blue' },
  { key: 'bloque_dette', label: 'Bloqués (dette)', color: 'red' },
  { key: 'bloque_absent', label: 'Bloqués (absent du fichier)', color: 'orange' },
];

const CONTRAT_TYPES = [
  { code: 'bourse', label: 'Bourse', icon: '🎓' },
  { code: 'convention', label: 'Convention', icon: '🤝' },
  { code: 'partenariat', label: 'Partenariat / Sponsor', icon: '💼' },
  { code: 'exonere', label: 'Exonéré', icon: '📜' },
  { code: 'autre', label: 'Autre', icon: '📝' },
];

type QuickFilter = '' | 'today' | 'week' | 'month' | 'never' | 'active';

function formatMoney(s: string): string {
  const n = parseFloat(s);
  if (isNaN(n) || n === 0) return '—';
  return new Intl.NumberFormat('fr-FR').format(n) + ' FCFA';
}

function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString('fr-FR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return '—';
  }
}

function timeAgo(iso: string | null | undefined): string {
  if (!iso) return 'Jamais';
  try {
    const d = new Date(iso).getTime();
    const now = Date.now();
    const diff = now - d;
    const min = Math.floor(diff / 60000);
    if (min < 1) return 'À l\'instant';
    if (min < 60) return `Il y a ${min} min`;
    const h = Math.floor(min / 60);
    if (h < 24) return `Il y a ${h}h`;
    const j = Math.floor(h / 24);
    if (j < 30) return `Il y a ${j}j`;
    const m = Math.floor(j / 30);
    return `Il y a ${m} mois`;
  } catch {
    return '—';
  }
}

const StatutBadge: React.FC<{
  mode: string; contratType?: string;
  // Fix 2026-09-06 : traçabilité accès forcé (nom + date + raison)
  acces_force_par_nom?: string;
  acces_force_date?: string | null;
  acces_force_raison?: string;
}> = ({ mode, contratType, acces_force_par_nom, acces_force_date, acces_force_raison }) => {
  const map: Record<string, { label: string; cls: string }> = {
    a_jour: { label: '✓ À jour', cls: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300' },
    sous_contrat: { label: '📝 Contrat', cls: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300' },
    force_admin: { label: '🔓 Forcé', cls: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300' },
    bloque_dette: { label: '⚠ Dette', cls: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300' },
    bloque_absent: { label: '✗ Absent', cls: 'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300' },
  };
  const item = map[mode] || { label: mode, cls: 'bg-gray-100 text-gray-800' };
  const ct = contratType ? CONTRAT_TYPES.find(c => c.code === contratType) : null;
  const dateFmt = acces_force_date
    ? new Date(acces_force_date).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })
    : '';
  return (
    <div className="inline-flex flex-col items-center gap-0.5">
      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${item.cls}`}
        title={
          mode === 'force_admin' && (acces_force_par_nom || dateFmt)
            ? `Forcé par : ${acces_force_par_nom || 'inconnu'}\n` +
              `Le : ${dateFmt || 'date inconnue'}\n` +
              `Raison : ${acces_force_raison || '—'}`
            : undefined
        }
      >
        {item.label}
      </span>
      {mode === 'sous_contrat' && ct && (
        <span className="text-[10px] text-amber-700 dark:text-amber-400">{ct.icon} {ct.label}</span>
      )}
      {mode === 'force_admin' && (acces_force_par_nom || dateFmt) && (
        <span className="text-[10px] text-blue-700 dark:text-blue-400 leading-tight text-center max-w-[140px]">
          {acces_force_par_nom && <>par {acces_force_par_nom}</>}
          {acces_force_par_nom && dateFmt && ' · '}
          {dateFmt}
        </span>
      )}
    </div>
  );
};

const KpiCard: React.FC<{
  label: string;
  value: number | string;
  hint?: string;
  color: string;
  icon: string;
  active?: boolean;
  onClick?: () => void;
}> = ({ label, value, hint, color, icon, active, onClick }) => (
  <button
    onClick={onClick}
    disabled={!onClick}
    className={`
      text-left p-3 rounded-lg border transition shadow-sm
      ${onClick ? 'cursor-pointer hover:shadow-md' : 'cursor-default'}
      ${active
        ? `bg-${color}-100 dark:bg-${color}-900/30 border-${color}-400 dark:border-${color}-600 ring-2 ring-${color}-300`
        : `bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700`
      }
    `}
  >
    <div className="flex items-center gap-2 mb-1">
      <span className="text-lg">{icon}</span>
      <div className="text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wide">{label}</div>
    </div>
    <div className={`text-2xl font-bold text-${color}-700 dark:text-${color}-400`}>{value}</div>
    {hint && <div className="text-xs text-gray-500 dark:text-gray-500 mt-0.5">{hint}</div>}
  </button>
);

// =====================================================================
// Modal Historique de connexions
// =====================================================================
const HistoriqueModal: React.FC<{
  etudiant: EtudiantAcces;
  onClose: () => void;
}> = ({ etudiant, onClose }) => {
  const [trigger, { data, isFetching }] = useLazyGetHistoriqueConnexionsQuery();

  useEffect(() => {
    trigger(etudiant.matricule);
  }, [etudiant.matricule, trigger]);

  const eventBadge = (event: string): string => ({
    login_ok: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
    login_fail: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
    login_bloque: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
    logout: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
  }[event] || 'bg-gray-100 text-gray-800');

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <div>
            <div className="font-semibold text-gray-900 dark:text-white">
              📜 Historique des connexions — {etudiant.nom} {etudiant.prenoms}
            </div>
            <div className="text-xs text-gray-500 mt-0.5">
              {etudiant.matricule} · {etudiant.classe}
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-200 text-2xl leading-none">×</button>
        </div>

        {data?.stats && (
          <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40">
            <div className="grid grid-cols-3 gap-3 text-center">
              <div>
                <div className="text-xs text-gray-500">Total</div>
                <div className="text-lg font-bold text-gray-700 dark:text-gray-200">{data.stats.nb_total}</div>
              </div>
              <div>
                <div className="text-xs text-green-600">Réussies</div>
                <div className="text-lg font-bold text-green-700 dark:text-green-300">{data.stats.nb_ok}</div>
              </div>
              <div>
                <div className="text-xs text-red-600">Échouées</div>
                <div className="text-lg font-bold text-red-700 dark:text-red-300">{data.stats.nb_fail}</div>
              </div>
            </div>
            {data.compte?.bloque && (
              <div className="mt-2 p-2 rounded bg-red-100 dark:bg-red-900/30 text-xs text-red-800 dark:text-red-200">
                🚫 Compte BLOQUÉ : {data.compte.bloque_raison}
              </div>
            )}
          </div>
        )}

        <div className="flex-1 overflow-y-auto p-3">
          {isFetching ? (
            <div className="text-center text-gray-400 text-sm py-8">Chargement...</div>
          ) : (data?.results || []).length === 0 ? (
            <div className="text-center text-gray-400 text-sm py-8">Aucune connexion enregistrée</div>
          ) : (
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-xs text-gray-500 dark:text-gray-400 uppercase border-b border-gray-200 dark:border-gray-700">
                  <th className="px-2 py-2 text-left">Date</th>
                  <th className="px-2 py-2 text-left">Événement</th>
                  <th className="px-2 py-2 text-left">IP</th>
                  <th className="px-2 py-2 text-left">Navigateur</th>
                </tr>
              </thead>
              <tbody>
                {(data?.results || []).map((c: ConnexionEvent) => (
                  <tr key={c.id} className="border-b border-gray-100 dark:border-gray-700/50">
                    <td className="px-2 py-2 text-xs whitespace-nowrap">{new Date(c.date).toLocaleString('fr-FR')}</td>
                    <td className="px-2 py-2">
                      <span className={`inline-flex px-2 py-0.5 rounded text-xs ${eventBadge(c.event)}`}>
                        {c.event_label}
                      </span>
                    </td>
                    <td className="px-2 py-2 font-mono text-xs text-gray-500">{c.ip || '—'}</td>
                    <td className="px-2 py-2 text-xs text-gray-500 truncate max-w-[200px]" title={c.user_agent}>
                      {c.user_agent ? c.user_agent.slice(0, 40) + (c.user_agent.length > 40 ? '…' : '') : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

// =====================================================================
// Modal Envoi message à un étudiant
// =====================================================================
const MessageModal: React.FC<{
  etudiant: EtudiantAcces;
  onClose: () => void;
}> = ({ etudiant, onClose }) => {
  const [titre, setTitre] = useState('');
  const [message, setMessage] = useState('');
  const [type, setType] = useState<'info' | 'success' | 'warning' | 'danger'>('info');
  const [sent, setSent] = useState(false);
  const [notifierMut, { isLoading }] = useNotifierEtudiantMutation();

  const submit = async () => {
    if (!titre.trim() || !message.trim()) return;
    try {
      await notifierMut({ matricule: etudiant.matricule, titre: titre.trim(), message: message.trim(), type }).unwrap();
      setSent(true);
      setTimeout(onClose, 1500);
    } catch (e: any) {
      alert(e?.data?.detail || 'Erreur');
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl max-w-md w-full" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <div className="font-semibold text-gray-900 dark:text-white">
            ✉️ Message à {etudiant.nom} {etudiant.prenoms}
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-200 text-2xl leading-none">×</button>
        </div>

        {sent ? (
          <div className="p-8 text-center">
            <div className="text-4xl mb-2">✅</div>
            <div className="text-gray-700 dark:text-gray-200">Message envoyé</div>
          </div>
        ) : (
          <div className="p-5 space-y-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Type</label>
              <div className="grid grid-cols-4 gap-1">
                {(['info', 'success', 'warning', 'danger'] as const).map((t) => (
                  <button
                    key={t}
                    onClick={() => setType(t)}
                    className={`text-xs py-1.5 rounded border ${
                      type === t
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30'
                        : 'border-gray-300 dark:border-gray-600'
                    }`}
                  >
                    {t === 'info' ? 'ℹ️' : t === 'success' ? '✅' : t === 'warning' ? '⚠️' : '🚨'} {t}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Titre</label>
              <input
                type="text"
                value={titre}
                onChange={(e) => setTitre(e.target.value)}
                maxLength={200}
                placeholder="Ex: Convocation, Information importante..."
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Message</label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={5}
                maxLength={2000}
                placeholder="Votre message..."
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
              <div className="text-xs text-gray-400 mt-0.5">{message.length}/2000</div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={onClose}
                className="flex-1 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded text-sm"
              >Annuler</button>
              <button
                onClick={submit}
                disabled={isLoading || !titre.trim() || !message.trim()}
                className="flex-1 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded text-sm font-medium"
              >{isLoading ? 'Envoi...' : '📨 Envoyer'}</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// =====================================================================
// Modal Broadcast (alerte à plusieurs étudiants)
// =====================================================================
const BroadcastModal: React.FC<{
  filtres: { pole?: string; niveau?: string; classe?: string; type_formation?: string };
  matricules: string[];
  totalFiltres: number;
  onClose: () => void;
}> = ({ filtres, matricules, totalFiltres, onClose }) => {
  const [titre, setTitre] = useState('');
  const [message, setMessage] = useState('');
  const [type, setType] = useState<'info' | 'success' | 'warning' | 'danger'>('info');
  const [useFiltres, setUseFiltres] = useState(true);
  const [sent, setSent] = useState<{ nb: number } | null>(null);
  const [broadcastMut, { isLoading }] = useBroadcastMessageMutation();

  const submit = async () => {
    if (!titre.trim() || !message.trim()) return;
    const nbCible = useFiltres ? totalFiltres : matricules.length;
    // Confirmation simple si <= 10. Au-delà, demander de taper "OUI" pour éviter
    // les envois massifs accidentels.
    if (nbCible <= 10) {
      const confirmMsg = useFiltres
        ? `Envoyer cette alerte aux ${totalFiltres} étudiants correspondant aux filtres ?`
        : `Envoyer cette alerte à ${matricules.length} étudiant(s) (liste affichée) ?`;
      if (!window.confirm(confirmMsg)) return;
    } else {
      const reponse = window.prompt(
        `⚠️ ENVOI MASSIF\n\n`
        + `Vous êtes sur le point d'envoyer cette alerte à ${nbCible} étudiants.\n\n`
        + `Cette opération est irréversible côté push (téléphones).\n`
        + `Pour confirmer, tapez exactement OUI puis OK.`
      );
      if (reponse?.trim().toUpperCase() !== 'OUI') {
        return;
      }
    }
    try {
      const body: any = { titre: titre.trim(), message: message.trim(), type };
      if (useFiltres) body.filtres = filtres;
      else body.matricules = matricules;
      const res = await broadcastMut(body).unwrap();
      setSent({ nb: res.nb_destinataires });
      setTimeout(onClose, 2000);
    } catch (e: any) {
      alert(e?.data?.detail || 'Erreur');
    }
  };

  const filtresActifs = Object.entries(filtres).filter(([_, v]) => v).map(([k, v]) => `${k}=${v}`).join(', ') || 'aucun';

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl max-w-md w-full" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <div className="font-semibold text-gray-900 dark:text-white">📢 Envoyer une alerte</div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-200 text-2xl leading-none">×</button>
        </div>

        {sent ? (
          <div className="p-8 text-center">
            <div className="text-4xl mb-2">✅</div>
            <div className="text-gray-700 dark:text-gray-200">Alerte envoyée à {sent.nb} étudiant(s)</div>
          </div>
        ) : (
          <div className="p-5 space-y-3">
            <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-700 rounded p-3 text-xs">
              <div className="font-semibold text-purple-800 dark:text-purple-200 mb-1">
                {useFiltres
                  ? `Tous les ${totalFiltres} étudiants correspondant aux filtres`
                  : `${matricules.length} destinataire(s) (liste affichée)`}
              </div>
              <div className="text-purple-700 dark:text-purple-300">
                Filtres actifs : {filtresActifs}
              </div>
            </div>
            <label className="flex items-center gap-2 text-xs text-gray-700 dark:text-gray-300 cursor-pointer">
              <input
                type="checkbox"
                checked={useFiltres}
                onChange={(e) => setUseFiltres(e.target.checked)}
                className="rounded"
              />
              Envoyer à <strong>tous les {totalFiltres}</strong> étudiants correspondant aux filtres
              (recommandé — sinon limité aux {matricules.length} affichés)
            </label>
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Type d'alerte</label>
              <div className="grid grid-cols-4 gap-1">
                {(['info', 'success', 'warning', 'danger'] as const).map((t) => (
                  <button
                    key={t}
                    onClick={() => setType(t)}
                    className={`text-xs py-1.5 rounded border ${
                      type === t
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30'
                        : 'border-gray-300 dark:border-gray-600'
                    }`}
                  >
                    {t === 'info' ? 'ℹ️' : t === 'success' ? '✅' : t === 'warning' ? '⚠️' : '🚨'}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Titre</label>
              <input
                type="text"
                value={titre}
                onChange={(e) => setTitre(e.target.value)}
                maxLength={200}
                placeholder="Ex: Maintenance, Convocation, Délibération..."
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Message</label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={5}
                maxLength={2000}
                placeholder="Votre message à diffuser..."
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
              <div className="text-xs text-gray-400 mt-0.5">{message.length}/2000</div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={onClose}
                className="flex-1 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded text-sm"
              >Annuler</button>
              <button
                onClick={submit}
                disabled={isLoading || !titre.trim() || !message.trim()}
                className="flex-1 py-2 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white rounded text-sm font-medium"
              >{isLoading
                ? 'Envoi...'
                : useFiltres
                  ? '📢 Envoyer à tous (filtres)'
                  : `📢 Envoyer à ${matricules.length}`}</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// =====================================================================
// Modal Encaissement de paiement à la caisse
// =====================================================================
const PERIODES_PAIEMENT = [
  // Ordre : périodes courantes (MAR, EXAM) en tête, puis chronologique de l'année académique (nov → août).
  { code: 'MAR', label: 'Mars' },
  { code: 'EXAM', label: 'Examen (2 semestres — 30 000 FCFA)' },
  { code: 'INSC', label: 'Inscription / Réinscription' },
  { code: 'NOV', label: 'Novembre' },
  { code: 'DEC', label: 'Décembre' },
  { code: 'JAN', label: 'Janvier' },
  { code: 'FEV', label: 'Février' },
  { code: 'AVR', label: 'Avril' },
  { code: 'MAI', label: 'Mai' },
  { code: 'JUIN', label: 'Juin' },
  { code: 'JUIL', label: 'Juillet' },
  { code: 'AOUT', label: 'Août' },
];

const EncaisserModal: React.FC<{
  etudiant: EtudiantAcces;
  onClose: () => void;
  onUpdated: () => void;
}> = ({ etudiant, onClose, onUpdated }) => {
  const [periode, setPeriode] = useState('MAR');
  const [montant, setMontant] = useState('');
  const [reference, setReference] = useState('');
  const [montantAttendu, setMontantAttendu] = useState('');
  const [remarque, setRemarque] = useState('');
  const [encaisserMut, { isLoading }] = useEncaisserPaiementMutation();
  const [success, setSuccess] = useState<{ ref: string; periode: string; ajour: boolean } | null>(null);

  const submit = async () => {
    const m = parseFloat(montant.replace(',', '.'));
    if (isNaN(m) || m <= 0) {
      alert('Montant invalide');
      return;
    }
    if (!reference.trim() || reference.trim().length < 3) {
      alert('N° de reçu requis (3 caractères min)');
      return;
    }
    try {
      const res = await encaisserMut({
        matricule: etudiant.matricule,
        periode,
        montant_paye: m,
        reference_recu: reference.trim(),
        montant_attendu: montantAttendu ? parseFloat(montantAttendu.replace(',', '.')) : undefined,
        remarque: remarque.trim() || undefined,
      }).unwrap();
      setSuccess({ ref: res.reference_recu, periode: res.periode, ajour: res.est_a_jour });
      onUpdated();
      setTimeout(onClose, 2500);
    } catch (e: any) {
      alert(e?.data?.detail || 'Erreur');
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl max-w-md w-full" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <div>
            <div className="font-semibold text-gray-900 dark:text-white">
              💵 Encaissement reçu — {etudiant.nom} {etudiant.prenoms}
            </div>
            <div className="text-xs text-gray-500 mt-0.5">{etudiant.matricule} · {etudiant.classe}</div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-200 text-2xl leading-none">×</button>
        </div>

        {success ? (
          <div className="p-8 text-center">
            <div className="text-5xl mb-3">{success.ajour ? '✅' : '⚠️'}</div>
            <div className="font-semibold text-gray-900 dark:text-white text-lg">
              Paiement enregistré
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400 mt-2">
              Référence : <span className="font-mono font-bold">{success.ref}</span><br/>
              Période : <span className="font-bold">{success.periode}</span><br/>
              Statut : {success.ajour ? '✓ À jour' : '⚠ Reste un solde'}
            </div>
            <div className="text-xs text-blue-600 dark:text-blue-400 mt-3">
              {success.ajour
                ? "L'étudiant peut maintenant se connecter au portail"
                : "L'étudiant doit encore payer le solde"}
            </div>
          </div>
        ) : (
          <div className="p-5 space-y-4">
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded p-3 text-xs text-green-800 dark:text-green-200">
              💡 Utilisez ce formulaire quand un étudiant <strong>vient de payer à la caisse</strong> et veut accéder
              à son relevé sans attendre l'import du fichier xlsx. Saisissez les informations du reçu papier.
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Période <span className="text-red-500">*</span>
                </label>
                <select
                  value={periode}
                  onChange={(e) => setPeriode(e.target.value)}
                  className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  {PERIODES_PAIEMENT.map((p) => (
                    <option key={p.code} value={p.code}>{p.code} — {p.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  N° de reçu <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={reference}
                  onChange={(e) => setReference(e.target.value)}
                  placeholder="Ex: REC-2026-1234"
                  className="w-full px-2 py-1.5 text-sm font-mono border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Montant payé (FCFA) <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  value={montant}
                  onChange={(e) => setMontant(e.target.value)}
                  placeholder="Ex: 150000"
                  min="0"
                  step="500"
                  className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Montant attendu <span className="text-gray-500">(optionnel)</span>
                </label>
                <input
                  type="number"
                  value={montantAttendu}
                  onChange={(e) => setMontantAttendu(e.target.value)}
                  placeholder="Si différent du montant payé"
                  min="0"
                  step="500"
                  className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                Remarque <span className="text-gray-500">(optionnel)</span>
              </label>
              <textarea
                value={remarque}
                onChange={(e) => setRemarque(e.target.value)}
                rows={2}
                maxLength={300}
                placeholder="Ex: Paiement partiel, complément à venir le 15/05"
                className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>

            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded p-2 text-xs text-blue-700 dark:text-blue-300">
              ℹ Si l'étudiant a déjà payé partiellement (xlsx précédent), le montant sera <strong>cumulé</strong>.
              Si le total atteint le montant attendu, l'étudiant devient automatiquement « à jour ».
            </div>

            <div className="flex gap-2">
              <button
                onClick={onClose}
                className="flex-1 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded text-sm"
              >Annuler</button>
              <button
                onClick={submit}
                disabled={isLoading || !montant || !reference}
                className="flex-1 py-2 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white rounded text-sm font-medium"
              >{isLoading ? 'Enregistrement...' : '✓ Enregistrer le paiement'}</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// =====================================================================
// Modal Contrat (bourse / convention / etc.)
// =====================================================================
const ContratModal: React.FC<{
  etudiant: EtudiantAcces;
  onClose: () => void;
  onUpdated: () => void;
}> = ({ etudiant, onClose, onUpdated }) => {
  const [type, setType] = useState(etudiant.contrat_type || 'bourse');
  const [remarque, setRemarque] = useState(etudiant.contrat_remarque || '');
  const [marquerMut, { isLoading }] = useMarquerContratMutation();

  const submit = async () => {
    if (!remarque.trim() || remarque.trim().length < 5) {
      alert('Remarque requise (5 caractères min)');
      return;
    }
    try {
      await marquerMut({
        matricule: etudiant.matricule,
        actif: true,
        type,
        remarque: remarque.trim(),
      }).unwrap();
      onUpdated();
      onClose();
    } catch (e: any) {
      alert(e?.data?.detail || 'Erreur');
    }
  };

  const retirer = async () => {
    if (!window.confirm(`Retirer le statut de contrat de ${etudiant.nom} ${etudiant.prenoms} ?\n\nL'accès dépendra de nouveau de sa situation de paiement.`)) return;
    try {
      await marquerMut({ matricule: etudiant.matricule, actif: false }).unwrap();
      onUpdated();
      onClose();
    } catch (e: any) {
      alert(e?.data?.detail || 'Erreur');
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl max-w-md w-full" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <div>
            <div className="font-semibold text-gray-900 dark:text-white">
              📝 Statut contrat — {etudiant.nom} {etudiant.prenoms}
            </div>
            <div className="text-xs text-gray-500 mt-0.5">{etudiant.matricule} · {etudiant.classe}</div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-200 text-2xl leading-none">×</button>
        </div>

        <div className="p-5 space-y-4">
          {etudiant.sous_contrat && (
            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-700 rounded p-3 text-xs">
              <div className="font-semibold text-amber-800 dark:text-amber-200 mb-1">⚠ Étudiant déjà sous contrat</div>
              <div className="text-amber-700 dark:text-amber-300">
                Type : <strong>{CONTRAT_TYPES.find(c => c.code === etudiant.contrat_type)?.label || etudiant.contrat_type}</strong><br/>
                Remarque : {etudiant.contrat_remarque}<br/>
                Par : {etudiant.contrat_par_nom}
              </div>
            </div>
          )}

          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded p-3 text-xs text-blue-800 dark:text-blue-200">
            💡 Les étudiants sous contrat (bourse, convention, partenariat...) accèdent au portail
            <strong> sans condition de paiement</strong>. Ils n'ont pas besoin d'apparaître dans le fichier xlsx.
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Type de contrat</label>
            <div className="grid grid-cols-2 gap-2">
              {CONTRAT_TYPES.map((t) => (
                <button
                  key={t.code}
                  onClick={() => setType(t.code)}
                  className={`p-2 text-xs text-left border rounded ${
                    type === t.code
                      ? 'border-amber-500 bg-amber-50 dark:bg-amber-900/30 ring-2 ring-amber-300'
                      : 'border-gray-300 dark:border-gray-600'
                  }`}
                >
                  <span className="text-base mr-1">{t.icon}</span>
                  <span className="font-medium">{t.label}</span>
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
              Remarque <span className="text-red-500">*</span> <span className="text-gray-500">(visible par tous les admins)</span>
            </label>
            <textarea
              value={remarque}
              onChange={(e) => setRemarque(e.target.value)}
              rows={3}
              maxLength={500}
              placeholder="Ex: Contrat n°2026-045, durée 1 an, 50% pris en charge par l'institut"
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
            <div className="text-xs text-gray-400 mt-0.5">{remarque.length}/500</div>
          </div>

          <div className="flex gap-2">
            {etudiant.sous_contrat && (
              <button
                onClick={retirer}
                disabled={isLoading}
                className="px-4 py-2 bg-red-100 hover:bg-red-200 dark:bg-red-900/30 text-red-700 dark:text-red-300 text-sm rounded font-medium"
              >🗑 Retirer le contrat</button>
            )}
            <button
              onClick={onClose}
              className="flex-1 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded text-sm"
            >Annuler</button>
            <button
              onClick={submit}
              disabled={isLoading || !remarque.trim() || remarque.trim().length < 5}
              className="flex-1 py-2 bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white rounded text-sm font-medium"
            >{isLoading ? 'Enregistrement...' : etudiant.sous_contrat ? '💾 Mettre à jour' : '✓ Activer le contrat'}</button>
          </div>
        </div>
      </div>
    </div>
  );
};

const POLES_LISTE = ['Polytechnique', 'Commerce', 'Juridique'];
const NIVEAUX_LISTE = ['L1', 'L2', 'L3', 'M1', 'M2'];
const TYPES_FORMATION = [
  { code: '', label: 'Toutes formations' },
  { code: 'FI', label: 'FI (Initiale)' },
  { code: 'CS', label: 'CS (Cours du soir)' },
  { code: 'DD', label: 'DD (Double diplôme)' },
];

export const GestionAcces: React.FC = () => {
  const [statut, setStatut] = useState('');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [pole, setPole] = useState('');
  const [niveau, setNiveau] = useState('');
  const [classe, setClasse] = useState('');
  const [typeFormation, setTypeFormation] = useState('');
  const [quickFilter, setQuickFilter] = useState<QuickFilter>('');
  const [sortBy, setSortBy] = useState<'last_login' | 'visites' | 'nom' | 'classe' | 'activite'>('activite');
  const [sortDesc, setSortDesc] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(false);
  // Limite progressive pour éviter de charger les 2000+ étudiants d'un coup
  // (réponse 1.3 MB, lent côté navigateur). 200 par défaut, bouton "Charger plus"
  const [pageLimit, setPageLimit] = useState(500);

  // Modals
  const [historiqueRec, setHistoriqueRec] = useState<EtudiantAcces | null>(null);
  const [messageRec, setMessageRec] = useState<EtudiantAcces | null>(null);
  const [contratRec, setContratRec] = useState<EtudiantAcces | null>(null);
  const [encaisserRec, setEncaisserRec] = useState<EtudiantAcces | null>(null);
  const [paiementRec, setPaiementRec] = useState<EtudiantAcces | null>(null);
  const [showBroadcast, setShowBroadcast] = useState(false);
  const [showAnnulerBroadcast, setShowAnnulerBroadcast] = useState(false);
  const [showDebloquer, setShowDebloquer] = useState(false);

  const { data, isLoading, refetch } = useListEtudiantsAccesQuery({
    statut: statut || undefined,
    search: search || undefined,
    pole: pole || undefined,
    niveau: niveau || undefined,
    classe: classe || undefined,
    type_formation: typeFormation || undefined,
    limit: pageLimit,
  });
  const [forcerMut] = useForcerAccesMutation();
  const [resetMut] = useResetPasswordEtudiantMutation();
  const [bloquerMut] = useBloquerEtudiantMutation();
  const [pwdResult, setPwdResult] = useState<{ matricule: string; password: string } | null>(null);

  // Auto-refresh every 20s pour suivre les connexions live
  useEffect(() => {
    if (!autoRefresh) return;
    const i = setInterval(() => refetch(), 20000);
    return () => clearInterval(i);
  }, [autoRefresh, refetch]);

  const matriculesEnLigne = useMemo(
    () => new Set(data?.kpis_temps_reel?.matricules_en_ligne || []),
    [data?.kpis_temps_reel?.matricules_en_ligne]
  );

  const allItems = data?.results || [];

  const stats = useMemo(() => {
    const now = Date.now();
    const todayStart = new Date(); todayStart.setHours(0, 0, 0, 0);
    const weekAgo = now - 7 * 24 * 3600 * 1000;
    const monthAgo = now - 30 * 24 * 3600 * 1000;

    let comptesCrees = 0;
    let connectesAujourdhui = 0;
    let connectesSemaine = 0;
    let connectesMois = 0;
    let jamaisConnectes = 0;
    let totalVisites = 0;

    for (const e of allItems) {
      if (e.compte_cree) comptesCrees++;
      totalVisites += e.nb_visites || 0;
      if (e.last_login) {
        const t = new Date(e.last_login).getTime();
        if (t >= todayStart.getTime()) connectesAujourdhui++;
        if (t >= weekAgo) connectesSemaine++;
        if (t >= monthAgo) connectesMois++;
      } else if (e.compte_cree) {
        jamaisConnectes++;
      }
    }
    return {
      total: allItems.length,
      comptesCrees,
      connectesAujourdhui,
      connectesSemaine,
      connectesMois,
      jamaisConnectes,
      totalVisites,
    };
  }, [allItems]);

  const filteredItems = useMemo(() => {
    const now = Date.now();
    const todayStart = new Date(); todayStart.setHours(0, 0, 0, 0);
    const weekAgo = now - 7 * 24 * 3600 * 1000;
    const monthAgo = now - 30 * 24 * 3600 * 1000;

    let arr = allItems;
    if (quickFilter === 'today') {
      arr = arr.filter(e => e.last_login && new Date(e.last_login).getTime() >= todayStart.getTime());
    } else if (quickFilter === 'week') {
      arr = arr.filter(e => e.last_login && new Date(e.last_login).getTime() >= weekAgo);
    } else if (quickFilter === 'month') {
      arr = arr.filter(e => e.last_login && new Date(e.last_login).getTime() >= monthAgo);
    } else if (quickFilter === 'never') {
      arr = arr.filter(e => e.compte_cree && !e.last_login);
    } else if (quickFilter === 'active') {
      arr = arr.filter(e => (e.nb_visites || 0) >= 3);
    }

    const sorted = [...arr].sort((a, b) => {
      let cmp = 0;
      if (sortBy === 'activite') {
        // En ligne en premier, puis par derniere_activite desc
        const aOnline = matriculesEnLigne.has(a.matricule) ? 1 : 0;
        const bOnline = matriculesEnLigne.has(b.matricule) ? 1 : 0;
        if (aOnline !== bOnline) return bOnline - aOnline;
        const ta = a.derniere_activite ? new Date(a.derniere_activite).getTime() : 0;
        const tb = b.derniere_activite ? new Date(b.derniere_activite).getTime() : 0;
        cmp = ta - tb;
      } else if (sortBy === 'last_login') {
        const ta = a.last_login ? new Date(a.last_login).getTime() : 0;
        const tb = b.last_login ? new Date(b.last_login).getTime() : 0;
        cmp = ta - tb;
      } else if (sortBy === 'visites') {
        cmp = (a.nb_visites || 0) - (b.nb_visites || 0);
      } else if (sortBy === 'nom') {
        cmp = `${a.nom} ${a.prenoms}`.localeCompare(`${b.nom} ${b.prenoms}`);
      } else if (sortBy === 'classe') {
        cmp = a.classe.localeCompare(b.classe);
      }
      return sortDesc ? -cmp : cmp;
    });
    return sorted;
  }, [allItems, quickFilter, sortBy, sortDesc, matriculesEnLigne]);

  const toggleSort = (col: 'last_login' | 'visites' | 'nom' | 'classe' | 'activite') => {
    if (sortBy === col) {
      setSortDesc(!sortDesc);
    } else {
      setSortBy(col);
      setSortDesc(true);
    }
  };

  const sortIcon = (col: string) => {
    if (sortBy !== col) return '⇅';
    return sortDesc ? '↓' : '↑';
  };

  const handleResetPwd = async (etu: EtudiantAcces) => {
    const raison = window.prompt(
      `Réinitialiser le mot de passe de ${etu.nom} ${etu.prenoms} ?\n\n`
      + 'Un nouveau mot de passe temporaire sera généré et affiché une seule fois.\n\n'
      + 'Raison (optionnel) :'
    );
    if (raison === null) return;
    try {
      const res = await resetMut({ matricule: etu.matricule, raison }).unwrap();
      setPwdResult({ matricule: res.matricule, password: res.nouveau_password });
      refetch();
    } catch (e: any) {
      alert(e?.data?.detail || 'Erreur');
    }
  };

  const handleBloquer = async (etu: EtudiantAcces) => {
    if (etu.bloque) {
      if (!window.confirm(`Débloquer l'accès portail de ${etu.nom} ${etu.prenoms} ?`)) return;
      try {
        await bloquerMut({ matricule: etu.matricule, bloquer: false }).unwrap();
        refetch();
      } catch (e: any) { alert(e?.data?.detail || 'Erreur'); }
    } else {
      const raison = window.prompt(
        `🚫 BLOQUER l'accès au portail pour ${etu.nom} ${etu.prenoms} ?\n\n`
        + 'L\'étudiant ne pourra plus se connecter. Une notification lui sera envoyée.\n\n'
        + 'Raison (obligatoire) :'
      );
      if (!raison || !raison.trim()) return;
      try {
        await bloquerMut({ matricule: etu.matricule, bloquer: true, raison }).unwrap();
        refetch();
      } catch (e: any) { alert(e?.data?.detail || 'Erreur'); }
    }
  };

  const handleForcer = async (etu: EtudiantAcces, activer: boolean) => {
    let raison = '';
    let motDePasse = '';
    if (activer) {
      raison = window.prompt(
        `Forcer l'accès au portail pour ${etu.nom} ${etu.prenoms} ?\n\nRaison (obligatoire) :`
      ) || '';
      if (!raison.trim()) return;
      // Fix 2026-09-06 : mot de passe de confirmation exigé par le backend
      // pour agents/caisse (super_admin/admin sont exemptés).
      motDePasse = window.prompt(
        `Mot de passe de confirmation admin :\n(Défini dans Paramètres > Général)`
      ) || '';
      if (!motDePasse.trim()) return;
    } else {
      if (!window.confirm(`Retirer l'accès forcé pour ${etu.nom} ${etu.prenoms} ?`)) return;
    }
    try {
      await forcerMut({
        matricule: etu.matricule,
        activer,
        raison,
        mot_de_passe: motDePasse || undefined,
      } as any).unwrap();
      refetch();
    } catch (e: any) {
      const code = e?.data?.code;
      if (code === 'mdp_non_configure') {
        alert('Aucun mot de passe de confirmation n\'est configuré.\n\n' +
              'Demandez à un administrateur de le définir dans :\n' +
              'Paramètres > Général > Confirmation "Forcer l\'accès"');
      } else if (code === 'mdp_incorrect') {
        alert('Mot de passe incorrect. Réessayez.');
      } else if (code === 'mdp_requis') {
        alert('Le mot de passe de confirmation est requis.');
      } else {
        alert(e?.data?.detail || 'Erreur');
      }
    }
  };

  const handleExportExcel = () => {
    const rows = filteredItems.map((e) => ({
      Matricule: e.matricule,
      Nom: e.nom,
      Prénoms: e.prenoms,
      Pôle: e.pole || '',
      Niveau: e.niveau || '',
      Option: e.option || '',
      Classe: e.classe,
      Formation: e.type_formation || '',
      Statut: e.mode,
      Autorisé: e.autorise ? 'Oui' : 'Non',
      'Solde (FCFA)': parseFloat(e.solde_total || '0'),
      'Compte créé': e.compte_cree ? 'Oui' : 'Non',
      'Nb visites': e.nb_visites || 0,
      'Dernière connexion': e.last_login ? formatDateTime(e.last_login) : 'Jamais',
      'Dernière IP': e.derniere_ip || '',
      'Accès forcé': e.acces_force ? 'Oui' : 'Non',
      'Aménagement': e.amenagement ? `Oui${(e.periodes_exemptees || []).length ? ` (non concernés : ${(e.periodes_exemptees || []).join(', ')})` : ''}` : 'Non',
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Accès portail');
    const ts = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(wb, `acces_portail_${ts}.xlsx`);
  };

  return (
    <div className="space-y-4">
      {historiqueRec && (
        <HistoriqueModal etudiant={historiqueRec} onClose={() => setHistoriqueRec(null)} />
      )}
      {messageRec && (
        <MessageModal etudiant={messageRec} onClose={() => setMessageRec(null)} />
      )}
      {contratRec && (
        <ContratModal
          etudiant={contratRec}
          onClose={() => setContratRec(null)}
          onUpdated={() => refetch()}
        />
      )}
      {encaisserRec && (
        <EncaisserModal
          etudiant={encaisserRec}
          onClose={() => setEncaisserRec(null)}
          onUpdated={() => refetch()}
        />
      )}
      {paiementRec && (
        <EtatPaiementModal etudiant={paiementRec} onClose={() => setPaiementRec(null)} />
      )}
      {showBroadcast && (
        <BroadcastModal
          filtres={{ pole, niveau, classe, type_formation: typeFormation }}
          matricules={filteredItems.map((e) => e.matricule)}
          totalFiltres={data?.total_filtres ?? filteredItems.length}
          onClose={() => setShowBroadcast(false)}
        />
      )}
      {showAnnulerBroadcast && (
        // eslint-disable-next-line react/jsx-no-undef
        <AnnulerBroadcastModal onClose={() => setShowAnnulerBroadcast(false)} />
      )}
      {showDebloquer && (
        <DebloquerTentativesModal onClose={() => setShowDebloquer(false)} />
      )}
      {pwdResult && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-sm w-full p-6">
            <h3 className="font-bold text-lg text-gray-900 dark:text-white mb-3">🔑 Mot de passe réinitialisé</h3>
            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3 text-xs text-yellow-800 dark:text-yellow-200 mb-4">
              ⚠ Ce mot de passe ne sera <strong>plus visible</strong> après cette fenêtre. Communiquez-le maintenant à l'étudiant.
            </div>
            <div className="space-y-2">
              <div>
                <div className="text-xs text-gray-500">Matricule</div>
                <div className="font-mono font-bold text-gray-900 dark:text-white">{pwdResult.matricule}</div>
              </div>
              <div>
                <div className="text-xs text-gray-500">Nouveau mot de passe</div>
                <div className="font-mono font-bold text-2xl text-blue-700 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 p-3 rounded select-all text-center tracking-widest">
                  {pwdResult.password}
                </div>
              </div>
              <button
                onClick={() => {
                  navigator.clipboard?.writeText(pwdResult.password);
                  alert('Copié ✓');
                }}
                className="w-full py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded text-sm"
              >
                📋 Copier
              </button>
            </div>
            <button
              onClick={() => setPwdResult(null)}
              className="mt-4 w-full py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg"
            >
              Fermer
            </button>
          </div>
        </div>
      )}

      {/* KPIs temps réel */}
      {data?.kpis_temps_reel && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <KpiCard
            label="🟢 En ligne maintenant"
            value={data.kpis_temps_reel.nb_en_ligne}
            hint={`Activité dans les ${data.kpis_temps_reel.seuil_online_min} dernières minutes`}
            color="green"
            icon=""
          />
          <KpiCard
            label="Comptes actifs"
            value={data.kpis_temps_reel.nb_comptes_actifs}
            hint="Avec mot de passe créé"
            color="indigo"
            icon="🆔"
          />
          <KpiCard
            label="Sous contrat"
            value={data.kpis_temps_reel.nb_sous_contrat ?? 0}
            hint="Bourse / Convention / Partenariat"
            color="amber"
            icon="📝"
            active={statut === 'sous_contrat'}
            onClick={() => setStatut(statut === 'sous_contrat' ? '' : 'sous_contrat')}
          />
          <KpiCard
            label="Bloqués (admin)"
            value={data.kpis_temps_reel.nb_bloques}
            hint="Accès portail refusé"
            color="red"
            icon="🚫"
          />
        </div>
      )}

      {/* KPIs filtres rapides */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <KpiCard
          label="Total étudiants"
          value={stats.total}
          color="gray"
          icon="👥"
          hint={`${stats.totalVisites} visites cumulées`}
          active={quickFilter === ''}
          onClick={() => setQuickFilter('')}
        />
        <KpiCard
          label="Comptes créés"
          value={stats.comptesCrees}
          hint={stats.total > 0 ? `${Math.round(stats.comptesCrees * 100 / stats.total)}%` : ''}
          color="indigo"
          icon="🆔"
        />
        <KpiCard
          label="Aujourd'hui"
          value={stats.connectesAujourdhui}
          hint="Connectés aujourd'hui"
          color="green"
          icon="🟢"
          active={quickFilter === 'today'}
          onClick={() => setQuickFilter(quickFilter === 'today' ? '' : 'today')}
        />
        <KpiCard
          label="7 derniers jours"
          value={stats.connectesSemaine}
          hint="Connectés cette semaine"
          color="blue"
          icon="📅"
          active={quickFilter === 'week'}
          onClick={() => setQuickFilter(quickFilter === 'week' ? '' : 'week')}
        />
        <KpiCard
          label="Jamais connectés"
          value={stats.jamaisConnectes}
          hint="Compte créé sans visite"
          color="orange"
          icon="⚪"
          active={quickFilter === 'never'}
          onClick={() => setQuickFilter(quickFilter === 'never' ? '' : 'never')}
        />
        <KpiCard
          label="Actifs"
          value={allItems.filter(e => (e.nb_visites || 0) >= 3).length}
          hint="≥ 3 visites"
          color="purple"
          icon="⭐"
          active={quickFilter === 'active'}
          onClick={() => setQuickFilter(quickFilter === 'active' ? '' : 'active')}
        />
      </div>

      {/* Répartition par pôle (badges cliquables) */}
      {data?.agregations?.par_pole && data.agregations.par_pole.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
          <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-2">Pôles</div>
          <div className="flex flex-wrap gap-2">
            {data.agregations.par_pole.map((p) => (
              <button
                key={p.pole}
                onClick={() => setPole(pole === p.pole ? '' : p.pole)}
                className={`px-3 py-1 text-sm rounded-full border ${
                  pole === p.pole
                    ? 'bg-purple-600 text-white border-purple-600'
                    : 'bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200'
                }`}
              >
                {p.pole} <span className="ml-1 font-bold">{p.nb}</span>
              </button>
            ))}
            {data.agregations.par_niveau.length > 0 && (
              <div className="border-l border-gray-300 dark:border-gray-600 mx-2"></div>
            )}
            {data.agregations.par_niveau.map((n) => (
              <button
                key={n.niveau}
                onClick={() => setNiveau(niveau === n.niveau ? '' : n.niveau)}
                className={`px-3 py-1 text-sm rounded-full border ${
                  niveau === n.niveau
                    ? 'bg-indigo-600 text-white border-indigo-600'
                    : 'bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200'
                }`}
              >
                {n.niveau} <span className="ml-1 font-bold">{n.nb}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Filtres + actions */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 border border-gray-200 dark:border-gray-700 space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <div className="lg:col-span-2">
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
              Recherche (matricule, nom)
            </label>
            <div className="flex gap-1">
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && setSearch(searchInput)}
                placeholder="Ex: 2IP25..."
                className="flex-1 px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
              />
              <button
                onClick={() => setSearch(searchInput)}
                className="px-2 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm"
              >🔍</button>
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Pôle</label>
            <select
              value={pole}
              onChange={(e) => setPole(e.target.value)}
              className="w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
            >
              <option value="">Tous</option>
              {POLES_LISTE.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Niveau</label>
            <select
              value={niveau}
              onChange={(e) => setNiveau(e.target.value)}
              className="w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
            >
              <option value="">Tous</option>
              {NIVEAUX_LISTE.map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Classe</label>
            <select
              value={classe}
              onChange={(e) => setClasse(e.target.value)}
              className="w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
            >
              <option value="">Toutes</option>
              {(data?.agregations?.classes || []).map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Formation</label>
            <select
              value={typeFormation}
              onChange={(e) => setTypeFormation(e.target.value)}
              className="w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
            >
              {TYPES_FORMATION.map((t) => <option key={t.code} value={t.code}>{t.label}</option>)}
            </select>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 items-center">
          <select
            value={statut}
            onChange={(e) => setStatut(e.target.value)}
            className="px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
          >
            {STATUTS.map((s) => (
              <option key={s.key} value={s.key}>{s.label}</option>
            ))}
          </select>
          {(pole || niveau || classe || typeFormation || statut || search) && (
            <button
              onClick={() => { setPole(''); setNiveau(''); setClasse(''); setTypeFormation(''); setStatut(''); setSearch(''); setSearchInput(''); }}
              className="text-xs px-3 py-1.5 bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-300 rounded"
            >✕ Effacer tous</button>
          )}
          <label className="flex items-center gap-1 text-xs text-gray-600 dark:text-gray-400">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
            />
            Auto-refresh 20s
          </label>
          <div className="ml-auto flex gap-2 flex-wrap">
            <button
              onClick={() => setShowBroadcast(true)}
              disabled={filteredItems.length === 0}
              className="px-3 py-1.5 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white rounded text-sm"
              title="Envoyer une alerte/message à tous les étudiants correspondant aux filtres"
            >
              📢 Alerte ({data?.total_filtres ?? filteredItems.length})
            </button>
            <button
              onClick={() => setShowAnnulerBroadcast(true)}
              className="px-3 py-1.5 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 border border-red-300 dark:border-red-700 hover:bg-red-200 dark:hover:bg-red-900/50 rounded text-sm"
              title="Annuler une alerte envoyée par erreur"
            >
              🗑️ Annuler une alerte
            </button>
            <button
              onClick={() => setShowDebloquer(true)}
              className="px-3 py-1.5 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 border border-amber-300 dark:border-amber-700 hover:bg-amber-200 dark:hover:bg-amber-900/50 rounded text-sm"
              title="Réinitialiser les tentatives d'un étudiant bloqué (rate-limit)"
            >
              🔓 Débloquer tentatives
            </button>
            <button
              onClick={() => refetch()}
              className="px-3 py-1.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded text-sm hover:bg-gray-200 dark:hover:bg-gray-600"
            >
              🔄 Rafraîchir
            </button>
            <button
              onClick={handleExportExcel}
              disabled={filteredItems.length === 0}
              className="px-3 py-1.5 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white rounded text-sm"
            >
              📥 Export Excel ({filteredItems.length})
            </button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-700 text-sm text-gray-600 dark:text-gray-400 flex justify-between items-center">
          <div>
            {isLoading ? 'Chargement...' : `${filteredItems.length} étudiant(s) affichés sur ${allItems.length}`}
            {quickFilter && (
              <button
                onClick={() => setQuickFilter('')}
                className="ml-2 text-xs px-2 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-full hover:bg-blue-200"
              >
                Effacer le filtre rapide ✕
              </button>
            )}
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-900">
              <tr>
                <th className="px-2 py-1.5 text-left text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase">Matricule</th>
                <th
                  className="px-2 py-1.5 text-left text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase cursor-pointer hover:text-blue-600"
                  onClick={() => toggleSort('nom')}
                >
                  Nom · Classe {sortIcon('nom')}
                </th>
                <th className="px-2 py-1.5 text-center text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase">Statut</th>
                <th className="px-2 py-1.5 text-right text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase">Solde</th>
                <th
                  className="px-2 py-1.5 text-center text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase cursor-pointer hover:text-blue-600"
                  onClick={() => toggleSort('visites')}
                  title="Nombre de visites du portail"
                >
                  Visites {sortIcon('visites')}
                </th>
                <th
                  className="px-2 py-1.5 text-left text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase cursor-pointer hover:text-blue-600"
                  onClick={() => toggleSort('activite')}
                  title="Activité récente · Dernière connexion · IP (au survol)"
                >
                  Activité {sortIcon('activite')}
                </th>
                <th className="px-2 py-1.5 text-center text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {filteredItems.map((e) => {
                const enLigne = matriculesEnLigne.has(e.matricule);
                const ipTitle = e.derniere_ip ? `IP : ${e.derniere_ip}` : '';
                const lastLoginTitle = e.last_login
                  ? `Dernière connexion : ${formatDateTime(e.last_login)} (${timeAgo(e.last_login)})`
                  : 'Jamais connecté';
                return (
                <tr key={e.matricule} className={`hover:bg-gray-50 dark:hover:bg-gray-900/40 ${e.bloque ? 'bg-red-50/30 dark:bg-red-900/10' : ''}`}>
                  <td className="px-2 py-1 text-xs font-mono text-gray-900 dark:text-gray-100 whitespace-nowrap">
                    {enLigne && <span className="inline-block w-1.5 h-1.5 bg-green-500 rounded-full mr-1 animate-pulse" title="En ligne" />}
                    {e.matricule}
                  </td>
                  <td className="px-2 py-1 text-xs text-gray-900 dark:text-gray-100">
                    <div className="leading-tight">
                      <span className="font-medium">{e.nom} {e.prenoms}</span>
                      {e.bloque && (
                        <span className="ml-1 text-[10px] px-1 py-0 rounded bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300" title={e.bloque_raison}>
                          bloqué
                        </span>
                      )}
                      {e.amenagement && (
                        <span
                          className="ml-1 text-[10px] px-1 py-0 rounded bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-300"
                          title={`Aménagement de paiement${(e.periodes_exemptees || []).length ? ` — non concernés : ${(e.periodes_exemptees || []).join(', ')}` : ' — montant exigé adapté'}`}
                        >
                          aménagé
                        </span>
                      )}
                    </div>
                    <div className="text-[10px] text-gray-500 dark:text-gray-400 leading-tight">
                      {e.classe}{e.pole ? ` · ${e.pole}` : ''}{e.type_formation && e.type_formation !== 'FI' ? ` · ${e.type_formation}` : ''}
                    </div>
                  </td>
                  <td className="px-2 py-1 text-center">
                    <StatutBadge
                      mode={e.mode}
                      contratType={e.contrat_type}
                      acces_force_par_nom={(e as any).acces_force_par_nom}
                      acces_force_date={(e as any).acces_force_date}
                      acces_force_raison={(e as any).acces_force_raison}
                    />
                    {e.acces_leger && (
                      <div
                        className="text-[10px] text-sky-700 dark:text-sky-300 leading-tight"
                        title={e.acces_leger_individuel
                          ? "Accès léger accordé individuellement : l'étudiant voit sa décision (validé ou non) sans ses notes"
                          : "Accès léger : l'étudiant voit sa décision (validé ou non) sans ses notes"}
                      >
                        accès léger
                      </div>
                    )}
                  </td>
                  <td className="px-2 py-1 text-xs text-right text-gray-700 dark:text-gray-300 whitespace-nowrap">{formatMoney(e.solde_total)}</td>
                  <td className="px-2 py-1 text-center text-xs">
                    {(e.nb_visites || 0) > 0 ? (
                      <span className={`inline-flex px-1.5 py-0 rounded font-semibold text-[11px] ${
                        (e.nb_visites || 0) >= 5
                          ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300'
                          : (e.nb_visites || 0) >= 2
                          ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
                          : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                      }`}>{e.nb_visites}</span>
                    ) : !e.compte_cree ? (
                      <span className="text-gray-400 text-[10px]" title="Compte non créé">○</span>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </td>
                  <td className="px-2 py-1 text-[11px]" title={`${lastLoginTitle}${ipTitle ? ' — ' + ipTitle : ''}`}>
                    {enLigne ? (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0 rounded-full bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-300 font-semibold animate-pulse text-[10px]">
                        En ligne
                      </span>
                    ) : e.derniere_activite ? (
                      <span className="text-gray-700 dark:text-gray-300">{timeAgo(e.derniere_activite)}</span>
                    ) : e.last_login ? (
                      <span className="text-gray-500 dark:text-gray-400">{timeAgo(e.last_login)}</span>
                    ) : (
                      <span className="text-gray-400 italic text-[10px]">Jamais</span>
                    )}
                  </td>
                  <td className="px-2 py-1">
                    <div className="flex gap-0.5 justify-end items-center flex-nowrap">
                      {e.acces_force ? (
                        <button
                          onClick={() => handleForcer(e, false)}
                          className="text-[11px] px-1.5 py-0.5 bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 hover:bg-orange-200 rounded"
                          title={
                            // Fix 2026-09-06 : traçabilité — qui a forcé, quand, pourquoi
                            (e as any).acces_force_par_nom || (e as any).acces_force_date
                              ? `Forcé par ${(e as any).acces_force_par_nom || 'inconnu'}\n` +
                                `Le ${(e as any).acces_force_date
                                  ? new Date((e as any).acces_force_date).toLocaleString('fr-FR', {
                                      dateStyle: 'short', timeStyle: 'short',
                                    })
                                  : 'date inconnue'}\n` +
                                `Raison : ${(e as any).acces_force_raison || '—'}\n\n` +
                                `Cliquer pour retirer l'accès forcé`
                              : 'Retirer l\'accès forcé'
                          }
                        >🔓→</button>
                      ) : !e.autorise ? (
                        <button
                          onClick={() => handleForcer(e, true)}
                          className="text-[11px] px-1.5 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 hover:bg-blue-200 rounded"
                          title="Forcer l'accès"
                        >🔓</button>
                      ) : null}
                      <button
                        onClick={() => setPaiementRec(e)}
                        className="text-[11px] px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600 rounded font-medium"
                        title="État de paiement : mois réglés, reliquats, mois non concernés"
                      >État</button>
                      <button
                        onClick={() => setEncaisserRec(e)}
                        className="text-[11px] px-1.5 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 hover:bg-green-200 rounded font-bold"
                        title="Encaisser un paiement (caisse) — débloquer l'accès"
                      >💵</button>
                      <button
                        onClick={() => setContratRec(e)}
                        className={`text-[11px] px-1.5 py-0.5 rounded ${
                          e.sous_contrat
                            ? 'bg-amber-200 dark:bg-amber-700 text-amber-900 dark:text-amber-100 hover:bg-amber-300'
                            : 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 hover:bg-amber-200'
                        }`}
                        title={e.sous_contrat ? `Contrat: ${e.contrat_type} — ${e.contrat_remarque}` : 'Marquer sous contrat (bourse, convention...)'}
                      >📝</button>
                      <button
                        onClick={() => setMessageRec(e)}
                        className="text-[11px] px-1.5 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 hover:bg-blue-200 rounded"
                        title="Envoyer un message"
                      >✉️</button>
                      <button
                        onClick={() => setHistoriqueRec(e)}
                        className="text-[11px] px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 rounded"
                        title="Historique des connexions"
                      >📜</button>
                      <button
                        onClick={() => handleBloquer(e)}
                        className={`text-[11px] px-1.5 py-0.5 rounded ${e.bloque
                          ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 hover:bg-green-200'
                          : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 hover:bg-red-200'
                        }`}
                        title={e.bloque ? 'Débloquer' : 'Bloquer le compte'}
                      >{e.bloque ? '✓' : '🚫'}</button>
                      {e.compte_cree && (
                        <button
                          onClick={() => handleResetPwd(e)}
                          className="text-[11px] px-1.5 py-0.5 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 hover:bg-purple-200 rounded"
                          title="Réinitialiser le mot de passe"
                        >🔑</button>
                      )}
                    </div>
                  </td>
                </tr>
                );
              })}
              {filteredItems.length === 0 && !isLoading && (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400 text-sm">
                    Aucun étudiant trouvé
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Bouton Charger plus si liste tronquée */}
        {!isLoading && data && data.total > allItems.length && (
          <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between bg-gray-50 dark:bg-gray-900/50">
            <div className="text-sm text-gray-600 dark:text-gray-400">
              {allItems.length} sur {data.total} étudiant(s) chargé(s).
              Affinez vos filtres ou cliquez pour charger plus.
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setPageLimit(pageLimit + 500)}
                className="px-3 py-1.5 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded font-medium"
              >
                Charger 500 de plus
              </button>
              <button
                onClick={() => setPageLimit(5000)}
                className="px-3 py-1.5 text-sm bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 rounded font-medium"
              >
                Tout charger ({data.total})
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// =====================================================================
// Modal "Annuler une alerte" — liste des derniers broadcasts envoyés
// =====================================================================
const AnnulerBroadcastModal: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const { data, isLoading, refetch } = useListBroadcastsRecentsQuery();
  const [annulerMut, { isLoading: annulant }] = useAnnulerBroadcastMutation();
  const [feedback, setFeedback] = useState<{ ok: boolean; text: string } | null>(null);

  const fmtDate = (s: string) => new Date(s).toLocaleString('fr-FR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });

  const annuler = async (id: string, titre: string, nb: number) => {
    const reponse = window.prompt(
      `⚠️ ANNULATION D'ALERTE\n\n`
      + `Vous êtes sur le point de supprimer du portail l'alerte « ${titre} » `
      + `qui a été envoyée à ${nb} étudiant(s).\n\n`
      + `IMPORTANT : les notifications push déjà délivrées sur les téléphones `
      + `ne peuvent pas être rappelées (limite technique). Seules les notifs `
      + `in-app (badge + liste portail) seront supprimées.\n\n`
      + `Pour confirmer, tapez exactement OUI puis OK.`
    );
    if (reponse?.trim().toUpperCase() !== 'OUI') return;
    setFeedback(null);
    try {
      const r = await annulerMut(id).unwrap();
      setFeedback({ ok: true, text: `${r.nb_supprimees} notifications supprimées du portail.` });
      refetch();
    } catch (e: any) {
      setFeedback({ ok: false, text: e?.data?.detail || e?.message || 'Erreur' });
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl max-w-2xl w-full max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <div className="font-semibold text-gray-900 dark:text-white">🗑️ Annuler une alerte</div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-200 text-2xl leading-none">×</button>
        </div>
        <div className="px-5 py-3 bg-amber-50 dark:bg-amber-900/20 border-b border-amber-200 dark:border-amber-800 text-xs text-amber-800 dark:text-amber-300">
          ⚠ L'annulation supprime les notifications du portail (badge + liste).
          Les pushs déjà délivrés sur les téléphones ne peuvent pas être rappelés.
        </div>
        {feedback && (
          <div className={`mx-5 mt-3 px-3 py-2 rounded text-sm ${
            feedback.ok
              ? 'bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-300 border border-green-200 dark:border-green-800'
              : 'bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-300 border border-red-200 dark:border-red-800'
          }`}>
            {feedback.text}
          </div>
        )}
        <div className="flex-1 overflow-y-auto p-5 space-y-2">
          {isLoading ? (
            <div className="text-center py-8 text-sm text-gray-500">Chargement…</div>
          ) : !data || data.results.length === 0 ? (
            <div className="text-center py-8 text-sm text-gray-500">
              Aucune alerte envoyée récemment.
            </div>
          ) : (
            data.results.map((b) => (
              <div
                key={b.broadcast_id}
                className="border border-gray-200 dark:border-gray-700 rounded-lg p-3 hover:border-gray-300 dark:hover:border-gray-600"
              >
                <div className="flex items-start justify-between gap-2 mb-1">
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm text-gray-900 dark:text-white truncate">
                      {b.titre || '(sans titre)'}
                    </div>
                    <div className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
                      {fmtDate(b.date_envoi)} ·{' '}
                      <strong>{b.nb_destinataires}</strong> destinataire(s) ·{' '}
                      {b.nb_lus} lu(s) · {b.nb_non_lus} non lu(s)
                    </div>
                  </div>
                  <button
                    onClick={() => annuler(b.broadcast_id, b.titre, b.nb_destinataires)}
                    disabled={annulant}
                    className="px-2.5 py-1 text-xs bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white rounded font-medium whitespace-nowrap"
                  >
                    Annuler
                  </button>
                </div>
                {b.message_extrait && (
                  <div className="text-xs text-gray-600 dark:text-gray-400 italic line-clamp-2 mt-1 pl-2 border-l-2 border-gray-200 dark:border-gray-700">
                    « {b.message_extrait} »
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};


/**
 * Modale : réinitialiser les tentatives d'un étudiant bloqué.
 * Endpoint : POST /api/portail/admin/reset-tentatives/
 */
const DebloquerTentativesModal: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const [matricule, setMatricule] = useState('');
  const [ip, setIp] = useState('');
  const [purgerToutes, setPurgerToutes] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setError(null);
    if (!matricule.trim() && !ip.trim() && !purgerToutes) {
      setError('Renseignez au moins un matricule, une IP, ou cochez la purge globale.');
      return;
    }
    setLoading(true);
    try {
      // Import dynamique pour éviter les cycles
      const { default: apiClient } = await import('../../config/api.config');
      const { data } = await apiClient.post('/portail/admin/reset-tentatives/', {
        matricule: matricule.trim().toUpperCase(),
        ip: ip.trim(),
        purger_toutes_creation: purgerToutes,
      });
      setResult(data);
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Erreur inconnue');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-lg w-full p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-lg text-gray-900 dark:text-white">
            🔓 Débloquer les tentatives d'un étudiant
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">×</button>
        </div>

        <div className="text-xs text-gray-600 dark:text-gray-400 mb-4 bg-blue-50 dark:bg-blue-900/20 p-2.5 rounded border border-blue-200 dark:border-blue-800">
          Utile quand un étudiant a épuisé les tentatives de création de compte, réinitialisation
          ou connexion (message « Trop de tentatives, réessayez dans X minutes »).
        </div>

        {result ? (
          <div className="space-y-3">
            <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-300 dark:border-emerald-700 rounded-lg p-3">
              <div className="text-emerald-800 dark:text-emerald-200 text-sm font-semibold mb-2">
                ✓ Déblocage effectué
              </div>
              <div className="text-xs text-emerald-700 dark:text-emerald-300 space-y-1">
                {result.compteur_login_reset > 0 && (
                  <div>Compteur login réinitialisé : {result.compteur_login_reset} → 0</div>
                )}
                <div>{result.nb_cles} clé(s) rate-limit purgée(s)</div>
                {result.cles_supprimees?.length > 0 && result.cles_supprimees.length < 10 && (
                  <div className="mt-2 text-[10px] font-mono text-gray-600 dark:text-gray-400">
                    {result.cles_supprimees.map((k: string, i: number) => (
                      <div key={i}>· {k}</div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-full py-2 bg-blue-600 hover:bg-blue-700 text-white rounded font-medium"
            >
              Fermer
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            <div>
              <label className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase mb-1 block">
                Matricule
              </label>
              <input
                type="text"
                value={matricule}
                onChange={(e) => setMatricule(e.target.value)}
                placeholder="Ex : 222IB3080"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded text-sm font-mono bg-white dark:bg-gray-700"
                autoCapitalize="characters"
              />
              <div className="text-[10px] text-gray-500 mt-1">
                Réinitialise le compteur DB (login) + la clé rate-limit login liée à ce matricule.
              </div>
            </div>

            <div>
              <label className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase mb-1 block">
                Adresse IP (facultatif)
              </label>
              <input
                type="text"
                value={ip}
                onChange={(e) => setIp(e.target.value)}
                placeholder="Ex : 102.141.40.129"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded text-sm font-mono bg-white dark:bg-gray-700"
              />
              <div className="text-[10px] text-gray-500 mt-1">
                Si l'étudiant connaît son IP, purger toutes les clés de création/reset liées à cette IP.
              </div>
            </div>

            <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={purgerToutes}
                  onChange={(e) => setPurgerToutes(e.target.checked)}
                  className="mt-0.5"
                />
                <div className="text-sm">
                  <div className="font-semibold text-amber-700 dark:text-amber-300">
                    Purger TOUTES les tentatives de création (dernier recours)
                  </div>
                  <div className="text-[10px] text-gray-500">
                    Efface les clés first_login / find_matricule / reset_init / reset_verify
                    pour tous les étudiants. Utile quand un étudiant ne connaît pas son IP.
                    Sans effet sur les comptes déjà activés.
                  </div>
                </div>
              </label>
            </div>

            {error && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-300 dark:border-red-700 rounded p-2 text-xs text-red-700 dark:text-red-300">
                {error}
              </div>
            )}

            <div className="flex gap-2 pt-2">
              <button
                onClick={onClose}
                className="flex-1 py-2 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 rounded font-medium"
              >
                Annuler
              </button>
              <button
                onClick={submit}
                disabled={loading}
                className="flex-1 py-2 bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white rounded font-medium"
              >
                {loading ? 'Déblocage…' : 'Débloquer'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
